import { Box, Button, Typography, useMediaQuery, Chip } from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import CustomLocationAlluser from "../customcomponents/CustomLocationAlluser";
import api from "../api";
import SearchInput from "../customcomponents/CustomSearchInput";
import SelectInput from "../customcomponents/CustomSelectInput";
import { useSocket } from "../contexts/SocketContext";
import GoogleMapPage from "./GoogleMapPage";
import { useLocation } from 'react-router-dom';

function MapPage() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery("(max-width:800px)");
  const { socket: socketContext, isConnected: isSocketConnected, on } = useSocket();
  const [locations, setLocations] = useState([]);
  const [mapStyle, setMapStyle] = useState('street'); // 'street' or 'satellite'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState('');

  const [patients, setPatients] = useState([]);
  const locationState = useLocation();
  const [broadcastMode, setBroadcastMode] = useState(false);
  const [broadcastServiceId, setBroadcastServiceId] = useState(null);
  const [broadcastPatientId, setBroadcastPatientId] = useState(null);


  useEffect(() => {
    if(!isSocketConnected) return;

    const fetchLocations = async () => {
      try {
        const response = await api.get("/location/getlocation");
        
        setLocations(response?.data?.locations);
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };

    fetchLocations();

    socketContext.on('locationData',(data)=> {
      setLocations(data)
      // console.log('locationData',data)
    })
  }, [socketContext,isSocketConnected]);


  // new useeffect for fetching patients with coords
  useEffect(() => {
  // If navigated with broadcast payload, enable broadcastMode and set filters
  try {
    const st = locationState?.state;
    if (st && st.fromAssignBroadcast) {
      setBroadcastMode(true);
      setBroadcastServiceId(st.serviceId || st.service || null);
      setBroadcastPatientId(st.patientId || st.patient || null);
    }
  } catch (e) {
    // ignore
  }

  let alive = true;

  (async () => {
    try {
      const res = await api.get('/patients/geo');
      if (!alive) return;

      const arr = Array.isArray(res.data?.data) ? res.data.data : [];

      // Ensure each patient has a role label to distinguish pins
      const withRole = arr.map(p => ({
        ...p,
        role: p.role || 'Patient',
      }));

      setPatients(withRole);
    } catch (e) {
      console.error('Failed to load patients with coords', e);
      setPatients([]);
    }
  })();

  return () => {
    alive = false;
  };
  }, []);


   // Build dropdown options for users
  const userOptions = useMemo(() => {
  const seen = new Set();
  const opts = [];

  locations.forEach(loc => {
    if (!seen.has(loc._id)) {
      seen.add(loc._id);
      opts.push({
        label: loc.TALM_UserName,
        value: loc._id,
      });
    }
  });

  return [
    { label: "All", value: "" },
    ...opts,
  ];
  }, [locations]);


  // Filter list by search + selected dropdown user
  const filteredLocations = useMemo(() => {
    return locations.filter(location => {
      const matchesSearch =
        !searchTerm ||
        location.TALM_UserName.toLowerCase().includes(searchTerm) ||
        location.TALM_User_Ref.TA_User_Role_Ref.TA_Role_Service_Ref.TA_Service_Name
          .toLowerCase()
          .includes(searchTerm);

      const matchesSelectedUser =
        !selectedUser || location._id === selectedUser;

      return matchesSearch && matchesSelectedUser;
    });
  }, [locations, searchTerm, selectedUser]);

  // console.log({locations})
  // console.log('filteredLocations',filteredLocations)
  //  locations to display on the map 
  const mockLocations = (filteredLocations || []).filter(location => location.TALM_isActive).map((location) => {
    return {
      id: location._id || '',
      name: location.TALM_UserName || '',
      address: location.TALM_Device_ID || '',
      email: (location.TALM_User_Ref && location.TALM_User_Ref.TA_User_Email) || location.TALM_UserEmail || location.TA_User_Email || location.TA_Patient_Email_Address || location.email || '',
      mobile: (location.TALM_User_Ref && location.TALM_User_Ref.TA_User_Mobile_Number) || location.TA_User_Mobile_Number || location.TA_Patient_Mobile_Number || location.mobile || '',
      role: (location.TALM_User_Ref && location.TALM_User_Ref.TA_User_Role_Ref && location.TALM_User_Ref.TA_User_Role_Ref.TA_Role_Name) || location.TALM_User_Role_Ref || location.TA_User_Role || location.role || '',
      latitude: location.TALM_Latitude || 0,
      longitude: location.TALM_Longitude || 0,
      serviceRef:location.TALM_User_Ref?.TA_User_Role_Ref.TA_Role_Service_Ref,
      url: '#'
    };
  });
  // console.log(mockLocations);

  // normalize the user data
const normalizedUsers = useMemo(() => {
  return (mockLocations || []).map(u => ({
    ...u,
    role: u.role || 'User',   // ensure role exists
  }));
}, [mockLocations]);

// combine user locations with patient locations
const combinedData = useMemo(() => {
  // If broadcastMode, filter vendors to only those matching the service and patients to the selected one
  if (broadcastMode) {
    const serviceMatches = (uService, bId) => {
      if (!bId || !uService) return false;
      const bid = String(bId);
      // Object with _id
      if (uService._id) return String(uService._id) === bid;
      // Array of services
      if (Array.isArray(uService)) {
        return uService.some(s => String(s._id || s) === bid);
      }
      // Plain id string
      if (typeof uService === 'string') return String(uService) === bid;
      // Fallback: try stringify _id-like fields
      if (uService && (uService.TA_Service_Name || uService.TA_Service_Amount)) {
        // populated service object without explicit _id (rare) - try _id
        if (uService._id) return String(uService._id) === bid;
      }
      return false;
    };

    const vendorList = (normalizedUsers || []).filter(u => {
      return serviceMatches(u.serviceRef, broadcastServiceId) || serviceMatches(u.serviceRef?._id, broadcastServiceId);
    });

    const patientList = (patients || []).filter(p => {
      const pid = p._id || p.id;
      return String(pid) === String(broadcastPatientId);
    });

    return [
      ...vendorList,
      ...patientList
    ];
  }

  return [
    ...normalizedUsers,
    ...patients,              
  ];
}, [normalizedUsers, patients, broadcastMode, broadcastServiceId, broadcastPatientId]);
  

  // Logic for handling search input
  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
    //setPage(0); // Reset to first page on new search
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {/* {isSmallScreen ? (
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 2,
            borderRadius: 3,
            background: theme.palette.mode == "dark" ? "#32373a" : "white",
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              flex: 1,
              textTransform: "capitalize",
              color: "background.text",
            }}
          >
            User Map
          </Typography>
          <SearchInput value={searchTerm} onChange={handleSearch} />
          <Box sx={{display:'flex' , gap:1 ,alignItems:'center',justifyContent:'center'}}>
          <SelectInput label="User" value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}
                options={userOptions}
          />
            <Button variant={mapStyle === 'street' ? 'contained' : 'outlined'} size="medium" onClick={() => setMapStyle('street')}>Map</Button>
            <Button variant={mapStyle === 'satellite' ? 'contained' : 'outlined'} size="medium" onClick={() => setMapStyle('satellite')}>Satellite</Button>
          </Box>
        </Box>
        ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: theme.palette.mode == "dark" ? "#32373a" : "white",
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
            mb: 3,
            gap: 2,
            flexWrap: "wrap",
            p: { xs: 1, sm: 3 },
            borderRadius: "12px",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              flex: 1,
              textTransform: "capitalize",
              color: theme.palette.mode === "dark" ? "white" : "black",
              mb: { xs: 2, md: 0 },
            }}
          >
            User Map
          </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <SearchInput value={searchTerm} onChange={handleSearch} />
               <SelectInput label="User" value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}
                    options={userOptions}
                />
              <Button variant={mapStyle === 'street' ? 'contained' : 'outlined'} size="small" onClick={() => setMapStyle('street')}>Map</Button>
              <Button variant={mapStyle === 'satellite' ? 'contained' : 'outlined'} size="small" onClick={() => setMapStyle('satellite')}>Satellite</Button>
            </Box>
        </Box>
      )} */}

      {/* map component here: wrapped container to make it attractive and fill available space */}
      {/* <GoogleMapPage data={mockLocations} /> */}
      <GoogleMapPage data={combinedData} />
    </Box>
  );
}

export default MapPage;
